import { useEffect, useRef } from 'react';
import { store, useMotion, useStudio } from '@/app/hooks';
import { L } from '@/i18n';
import { toast } from '@/ui/toast';

/**
 * Atmospheric background, built to stay cheap on large screens:
 *  - clouds are soft radial gradients moved with transforms only (no blur filters,
 *    no blend modes — those force full-screen repaints every frame);
 *  - particles are pre-rendered glow sprites drawn at ≤30 fps at 1× resolution;
 *  - nothing animates when motion is off/reduced or the tab is hidden.
 * If frames are consistently slow and automatic effects are allowed, Studio steps
 * the effect level down once and says so.
 */

const LEVEL_PARTICLES = { off: 0, low: 0, medium: 26, high: 54 } as const;
const STEP_DOWN = { high: 'medium', medium: 'low', low: 'off', off: 'off' } as const;

function glowSprite(hue: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = c.height = 16;
  const g = c.getContext('2d')!;
  const grad = g.createRadialGradient(8, 8, 0, 8, 8, 8);
  grad.addColorStop(0, `hsla(${hue}, 100%, 85%, 1)`);
  grad.addColorStop(0.25, `hsla(${hue}, 100%, 70%, 0.6)`);
  grad.addColorStop(1, `hsla(${hue}, 100%, 60%, 0)`);
  g.fillStyle = grad;
  g.fillRect(0, 0, 16, 16);
  return c;
}

export function Atmosphere() {
  const canvas = useRef<HTMLCanvasElement>(null);
  const motion = useMotion();
  const effects = useStudio((s) => s.settings.effects);
  const auto = useStudio((s) => s.settings.effectsAuto);

  useEffect(() => {
    const el = canvas.current;
    const count = LEVEL_PARTICLES[effects];
    if (!el || count === 0) return;
    const ctx = el.getContext('2d');
    if (!ctx) return;
    const sprites = [glowSprite(190), glowSprite(265)];
    let w = 0, h = 0, raf = 0, last = 0;
    type P = { x: number; y: number; vx: number; vy: number; s: number; a: number; k: number };
    let ps: P[] = [];
    const streaks: { x: number; y: number; len: number; life: number }[] = [];
    const resize = () => {
      w = el.clientWidth;
      h = el.clientHeight;
      el.width = w;
      el.height = h;
      ps = Array.from({ length: count }, () => ({
        x: Math.random() * w, y: Math.random() * h, vx: (Math.random() - 0.5) * 0.25, vy: -0.1 - Math.random() * 0.35,
        s: 4 + Math.random() * 7, a: 0.25 + Math.random() * 0.55, k: Math.random() < 0.7 ? 0 : 1,
      }));
    };
    resize();
    window.addEventListener('resize', resize);

    // frame-time monitor (only while animating)
    let samples = 0, slow = 0, downgraded = false;

    const draw = (t: number) => {
      raf = requestAnimationFrame(draw);
      const dt = t - last;
      if (dt < 32) return; // ~30 fps is plenty for ambient motion
      if (last) {
        samples++;
        if (dt > 70) slow++;
        if (auto && !downgraded && samples >= 90 && slow / samples > 0.35) {
          downgraded = true;
          const next = STEP_DOWN[effects];
          store.dispatch({ type: 'SETTINGS_CHANGED', patch: { effects: next } });
          toast(L('Background effects lowered to keep Studio smooth. Change it in Settings.', 'Bakgrunnseffektene ble redusert for at Studio skal gå jevnt. Du kan endre dette i Innstillinger.'));
        }
        if (samples > 300) { samples = 0; slow = 0; }
      }
      last = t;
      ctx.clearRect(0, 0, w, h);
      for (const p of ps) {
        p.x += p.vx;
        p.y += p.vy;
        if (p.y < -10) { p.y = h + 10; p.x = Math.random() * w; }
        if (p.x < -10) p.x = w + 10;
        if (p.x > w + 10) p.x = -10;
        ctx.globalAlpha = p.a;
        ctx.drawImage(sprites[p.k], p.x - p.s / 2, p.y - p.s / 2, p.s, p.s);
      }
      ctx.globalAlpha = 1;
      if (effects === 'high') {
        if (Math.random() < 0.01 && streaks.length < 2) streaks.push({ x: Math.random() * w, y: Math.random() * h * 0.6, len: 120 + Math.random() * 180, life: 1 });
        for (let i = streaks.length - 1; i >= 0; i--) {
          const s = streaks[i];
          const g = ctx.createLinearGradient(s.x, s.y, s.x + s.len, s.y + s.len * 0.25);
          g.addColorStop(0, 'rgba(70,230,255,0)');
          g.addColorStop(0.5, `rgba(120,220,255,${0.45 * s.life})`);
          g.addColorStop(1, 'rgba(162,131,255,0)');
          ctx.strokeStyle = g;
          ctx.lineWidth = 1.2;
          ctx.beginPath();
          ctx.moveTo(s.x, s.y);
          ctx.lineTo(s.x + s.len, s.y + s.len * 0.25);
          ctx.stroke();
          s.x += 10; s.y += 2.5; s.life -= 0.04;
          if (s.life <= 0) streaks.splice(i, 1);
        }
      }
    };

    const animate = motion === 'full';
    const start = () => {
      cancelAnimationFrame(raf);
      last = 0;
      if (animate && !document.hidden) raf = requestAnimationFrame(draw);
    };
    if (animate) start();
    else {
      draw(performance.now()); // one static frame
      cancelAnimationFrame(raf);
    }
    const onVis = () => (document.hidden ? cancelAnimationFrame(raf) : start());
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [motion, effects, auto]);

  return (
    <div className="atmosphere" aria-hidden="true">
      <div className="cloud c1" /><div className="cloud c2" /><div className="cloud c3" />
      <div className="grid" />
      <div className="horizon" />
      <canvas ref={canvas} />
      <div className="sweep" />
      <div className="scan" />
      <div className="vignette" />
    </div>
  );
}
